import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, ExternalLink, QrCode } from "lucide-react";
import { useAppSelector } from "../../app/hooks";
import { selectBookingPageSettings } from "../../features/trial-requests/trial-requests-slice";
import {
  bookingLinksUrl,
  buildBookingPageQrSvg,
  svgToDataUrl,
} from "../../features/trial-requests/utils/bookingPage";

/**
 * Share card for the link-in-bio page: one URL + QR code that bundles the
 * instructor's social links with a booking call-to-action — the link to put
 * in an Instagram bio or on a flyer.
 */
const WebsiteLinksPageCard = () => {
  const { t } = useTranslation();
  const settings = useAppSelector(selectBookingPageSettings);
  const [copied, setCopied] = useState(false);

  const linksUrl = settings?.slug ? bookingLinksUrl(settings.slug) : null;
  const qrDataUrl = useMemo(
    () => (linksUrl ? svgToDataUrl(buildBookingPageQrSvg(linksUrl)) : null),
    [linksUrl],
  );

  if (!settings?.enabled || !linksUrl) {
    return null;
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(linksUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl || !settings.slug) return;
    // Rasterize the SVG at print resolution; fall back to the SVG itself.
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const context = canvas.getContext("2d");
      if (!context) return triggerDownload(qrDataUrl, `links-page-${settings.slug}.svg`);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      triggerDownload(canvas.toDataURL("image/png"), `links-page-${settings.slug}.png`);
    };
    image.onerror = () => triggerDownload(qrDataUrl, `links-page-${settings.slug}.svg`);
    image.src = qrDataUrl;
  };

  const triggerDownload = (href: string, filename: string) => {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = filename;
    anchor.click();
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.linksPageTitle", "Your links page")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "website.linksPageIntro",
          "One beautiful page with all your links and a booking button — perfect for your Instagram bio or a single QR code. It uses your accent color, photo and the links below.",
        )}
      </p>

      <div className="mt-4 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-white">
          {linksUrl}
        </code>
        <button type="button" className="btn btn-secondary" onClick={handleCopy}>
          <Copy size={16} />
          {copied ? t("trialRequests.copied", "Copied!") : t("trialRequests.copyLink", "Copy link")}
        </button>
      </div>

      {qrDataUrl && (
        <div className="mt-4 flex items-center gap-4">
          {/* QR codes need a light background to scan — the white chip stays in dark mode too. */}
          <img
            src={qrDataUrl}
            alt={t("website.linksPageQrAlt", "QR code linking to your links page")}
            className="h-24 w-24 rounded-lg border border-grey-90 bg-white p-1 dark:border-grey-50"
          />
          <div>
            <button type="button" className="btn btn-secondary" onClick={handleDownloadQr}>
              <QrCode size={16} />
              {t("trialRequests.qrDownload", "Download QR code")}
            </button>
            <a
              href={linksUrl}
              target="_blank"
              rel="noopener"
              className="mt-2 flex items-center gap-1 text-sm text-primary-40 hover:underline dark:text-primary-90"
            >
              <ExternalLink size={14} />
              {t("website.linksPagePreview", "Preview your links page")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteLinksPageCard;
